import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";

const USER_TYPES = ["TRAINEE_ENTREPRENEUR", "ENTREPRENEUR"];

const safe = (v, fb = "-") =>
  v === null || v === undefined || String(v).trim() === "" ? fb : v;

export default function AdminUsers() {
  const navigate = useNavigate();

  // list
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // search (type-to-search)
  const [search, setSearch] = useState("");

  // drawer (view)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  // ✅ show/hide password in drawer
  const [viewPwdShow, setViewPwdShow] = useState(false);

  // edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(getEmptyEditForm());
  const [editSaving, setEditSaving] = useState(false);

  // ✅ show/hide password in edit modal
  const [editPwdShow, setEditPwdShow] = useState(false);

  // ✅ profile pic (file + preview)
  const [picFile, setPicFile] = useState(null);
  const [picPreview, setPicPreview] = useState("");
  // User roles & filters
  const [userRole, setUserRole] = useState(""); // ✅ Added userRole state
  const [filterRole, setFilterRole] = useState("USER"); // ✅ "USER" or "OTHERS"
  const [filterStatus, setFilterStatus] = useState("ALL"); // ✅ "ALL", "ACTIVE", "INACTIVE"

  // ✅ Add Admin/Staff
  const [addOpen, setAddOpen] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  const [addPwdShow, setAddPwdShow] = useState(false);
  const [addForm, setAddForm] = useState({
    name: "", email: "", phone: "", password: "", role: "STAFF",
    gender: "", dateOfBirth: "",
    bankAccountNumber: "", ifscCode: "", accountHolderName: "",
    bankName: "", bankBranch: "", bankAccountType: "",
    panNumber: "", adharNumber: "", upiId: "",
    nomineeName: "", nomineeRelation: "", nomineePhone: "",
  });
  // add form photo states
  const [addBankPhotoFile, setAddBankPhotoFile] = useState(null);
  const [addBankPhotoPreview, setAddBankPhotoPreview] = useState("");
  const [addPanPhotoFile, setAddPanPhotoFile] = useState(null);
  const [addPanPhotoPreview, setAddPanPhotoPreview] = useState("");
  const [addAadharPhotoFile, setAddAadharPhotoFile] = useState(null);
  const [addAadharPhotoPreview, setAddAadharPhotoPreview] = useState("");
  const [addPicFile, setAddPicFile] = useState(null);
  const [addPicPreview, setAddPicPreview] = useState("");

  // ✅ New photo states
  const [bankPhotoFile, setBankPhotoFile] = useState(null);
  const [bankPhotoPreview, setBankPhotoPreview] = useState("");
  const [panPhotoFile, setPanPhotoFile] = useState(null);
  const [panPhotoPreview, setPanPhotoPreview] = useState("");
  const [aadharPhotoFile, setAadharPhotoFile] = useState(null);
  const [aadharPhotoPreview, setAadharPhotoPreview] = useState("");

  // ✅ helper: build absolute image url (works with /uploads/..)
  const API_ORIGIN = useMemo(() => {
    const base = (axiosInstance.defaults.baseURL || "").trim();
    if (!base) return window.location.origin;
    try {
      return new URL(base).origin;
    } catch {
      return window.location.origin;
    }
  }, []);

  const buildImgUrl = (path) => {
    if (!path) return "";
    const p = String(path);
    if (p.startsWith("http://") || p.startsWith("https://")) return p;
    if (p.startsWith("/")) return `${API_ORIGIN}${p}`;
    return `${API_ORIGIN}/${p}`;
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const user = JSON.parse(localStorage.getItem("user") || "null");
    const r = (user?.role || "").toUpperCase();

    if (!token || !(r === "ADMIN" || r === "STAFF")) {
      navigate("/login", { replace: true });
      return;
    }

    setUserRole(r); // ✅ Set userRole state

    fetchUsers(""); // initial load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async (q = search) => {
    try {
      setLoading(true);

      const query = (q || "").trim();

      const roleParam = filterRole === "USER" ? "USER" : "ADMIN,STAFF";

      const res = await axiosInstance.get("/api/users", {
        params: {
          search: query || undefined,
          role: roleParam, // ✅ Filter by role
        },
      });

      const data = res.data;
      setAllUsers(Array.isArray(data.users) ? data.users : []);
    } catch (err) {
      console.log("GET /api/users error", err);
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        navigate("/admin", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  };

  // ✅ Typing lo auto-search (debounce)
  useEffect(() => {
    const t = setTimeout(() => {
      fetchUsers(search);
    }, 350);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filterRole]); // ✅ Added filterRole to dependencies

  // ✅ Instant client-side filter (fast UI)
  const users = useMemo(() => {
    // 1. First filter by Role (Safeguard if API returns more)
    let list = allUsers;
    if (filterRole === "USER") {
      list = allUsers.filter((u) => {
        const r = String(u.role || "").toUpperCase();
        const ut = String(u.userType || "").toUpperCase();
        // Strictly exclude ADMIN and STAFF from this list
        if (r === "ADMIN" || r === "STAFF" || ut === "ADMIN" || ut === "STAFF") {
          return false;
        }
        return true;
      });
    } else {
      list = allUsers.filter((u) => {
        const r = String(u.role || "").toUpperCase();
        const ut = String(u.userType || "").toUpperCase();
        // ONLY allow ADMIN or STAFF
        return r === "ADMIN" || r === "STAFF" || ut === "ADMIN" || ut === "STAFF";
      });
    }

    // 2. Filter by Status
    if (filterStatus !== "ALL") {
      list = list.filter((u) => {
        const s = String(u.status || "ACTIVE").toUpperCase();
        return filterStatus === "ACTIVE" ? s !== "INACTIVE" : s === "INACTIVE";
      });
    }

    const q = search.trim().toLowerCase();
    if (!q) return list;

    return list.filter((u) => {
      const name = (u.name || "").toLowerCase();
      const email = (u.email || "").toLowerCase();
      const phone = (u.phone || "").toLowerCase();
      const userType = (u.userType || "").toLowerCase();
      const userID = (u.userID || "").toLowerCase();
      return (
        name.includes(q) ||
        email.includes(q) ||
        phone.includes(q) ||
        userType.includes(q) ||
        userID.includes(q)
      );
    });
  }, [allUsers, search, filterRole, filterStatus]); // ✅ Added filterRole and filterStatus to dependencies

  const total = users.length;

  // ---------- VIEW (Drawer) ----------
  const openView = async (id) => {
    try {
      setDrawerOpen(true);
      setViewLoading(true);
      setSelectedUser(null);
      setViewPwdShow(false);

      const res = await axiosInstance.get(`/api/users/${id}`);
      setSelectedUser(res.data);
    } catch (err) {
      console.log("GET /api/users/:id error", err);
    } finally {
      setViewLoading(false);
    }
  };

  const closeView = () => {
    setDrawerOpen(false);
    setSelectedUser(null);
    setViewPwdShow(false);
  };

  // ---------- EDIT (Modal) ----------
  const openEdit = async (userRow) => {
    try {
      // try full details (so bank details also come)
      const res = await axiosInstance.get(`/api/users/${userRow.id}`);
      const u = res?.data || userRow;

      setEditForm({
        name: u?.name || "",
        email: u?.email || "",
        phone: u?.phone || "",
        userType: u?.userType || "TRAINEE_ENTREPRENEUR",
        password: u?.password || "",

        // ✅ NEW fields
        userID: u?.userID || "",
        bankAccountNumber: u?.bankAccountNumber || "",
        ifscCode: u?.ifscCode || "",
        accountHolderName: u?.accountHolderName || "",
        panNumber: u?.panNumber || "",
        upiId: u?.upiId || "",
        status: u?.status || "ACTIVE",

        // ✅ Missing fields
        gender: u?.gender || "",
        dateOfBirth: u?.dateOfBirth || "",
        nomineeName: u?.nomineeName || "",
        nomineeRelation: u?.nomineeRelation || "",
        nomineePhone: u?.nomineePhone || "",
        bankName: u?.bankName || "",
        bankBranch: u?.bankBranch || "",
        bankAccountType: u?.bankAccountType || "",
        adharNumber: u?.adharNumber || "",

        // wallet (read-only here)
        walletBalance: u?.walletBalance ?? u?.wallet?.balance ?? "",
      });

      setEditPwdShow(false);

      setPicFile(null);
      setPicPreview(u?.profilePic ? buildImgUrl(u.profilePic) : "");

      setBankPhotoFile(null);
      setBankPhotoPreview(u?.bankPhoto ? buildImgUrl(u.bankPhoto) : "");
      setPanPhotoFile(null);
      setPanPhotoPreview(u?.panPhoto ? buildImgUrl(u.panPhoto) : "");
      setAadharPhotoFile(null);
      setAadharPhotoPreview(u?.aadharPhoto ? buildImgUrl(u.aadharPhoto) : "");

      setSelectedUser(u);
      setEditOpen(true);
    } catch (err) {
      console.log("openEdit (fallback) error", err);

      // fallback to row
      setEditForm({
        name: userRow?.name || "",
        email: userRow?.email || "",
        phone: userRow?.phone || "",
        userType: userRow?.userType || "TRAINEE_ENTREPRENEUR",
        password: userRow?.password || "",

        userID: userRow?.userID || "",
        bankAccountNumber: userRow?.bankAccountNumber || "",
        ifscCode: userRow?.ifscCode || "",
        accountHolderName: userRow?.accountHolderName || "",
        panNumber: userRow?.panNumber || "",
        upiId: userRow?.upiId || "",
        status: userRow?.status || "ACTIVE",

        // ✅ Missing fields fallback
        gender: userRow?.gender || "",
        dateOfBirth: userRow?.dateOfBirth || "",
        nomineeName: userRow?.nomineeName || "",
        nomineeRelation: userRole?.nomineeRelation || "",
        nomineePhone: userRow?.nomineePhone || "",
        bankName: userRow?.bankName || "",
        bankBranch: userRow?.bankBranch || "",
        bankAccountType: userRow?.bankAccountType || "",
        adharNumber: userRow?.adharNumber || "",

        walletBalance: userRow?.walletBalance ?? userRow?.wallet?.balance ?? "",
      });
      setEditPwdShow(false);
      setPicFile(null);
      setPicPreview(userRow?.profilePic ? buildImgUrl(userRow.profilePic) : "");
      setBankPhotoFile(null);
      setBankPhotoPreview(userRow?.bankPhoto ? buildImgUrl(userRow.bankPhoto) : "");
      setPanPhotoFile(null);
      setPanPhotoPreview(userRow?.panPhoto ? buildImgUrl(userRow.panPhoto) : "");
      setAadharPhotoFile(null);
      setAadharPhotoPreview(userRow?.aadharPhoto ? buildImgUrl(userRow.aadharPhoto) : "");
      setSelectedUser(userRow);
      setEditOpen(true);
    }
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditForm(getEmptyEditForm());
    setEditPwdShow(false);
    setPicFile(null);
    setPicPreview("");
    setBankPhotoFile(null);
    setBankPhotoPreview("");
    setPanPhotoFile(null);
    setPanPhotoPreview("");
    setAadharPhotoFile(null);
    setAadharPhotoPreview("");
  };

  const onPickPic = (e) => {
    const file = e.target.files?.[0] || null;
    setPicFile(file);
    if (!file) {
      setPicPreview(selectedUser?.profilePic ? buildImgUrl(selectedUser.profilePic) : "");
      return;
    }
    setPicPreview(URL.createObjectURL(file));
  };

  const onPickBankPhoto = (e) => {
    const file = e.target.files?.[0] || null;
    setBankPhotoFile(file);
    if (!file) {
      setBankPhotoPreview(selectedUser?.bankPhoto ? buildImgUrl(selectedUser.bankPhoto) : "");
      return;
    }
    setBankPhotoPreview(URL.createObjectURL(file));
  };

  const onPickPanPhoto = (e) => {
    const file = e.target.files?.[0] || null;
    setPanPhotoFile(file);
    if (!file) {
      setPanPhotoPreview(selectedUser?.panPhoto ? buildImgUrl(selectedUser.panPhoto) : "");
      return;
    }
    setPanPhotoPreview(URL.createObjectURL(file));
  };

  const onPickAadharPhoto = (e) => {
    const file = e.target.files?.[0] || null;
    setAadharPhotoFile(file);
    if (!file) {
      setAadharPhotoPreview(selectedUser?.aadharPhoto ? buildImgUrl(selectedUser.aadharPhoto) : "");
      return;
    }
    setAadharPhotoPreview(URL.createObjectURL(file));
  };

  const saveEdit = async () => {
    if (!selectedUser?.id) return;

    try {
      setEditSaving(true);

      const hasFiles = picFile || bankPhotoFile || panPhotoFile || aadharPhotoFile;

      if (hasFiles) {
        const fd = new FormData();
        // Base fields
        fd.append("name", editForm.name || "");
        fd.append("email", editForm.email || "");
        fd.append("phone", editForm.phone || "");
        fd.append("userType", editForm.userType || "");
        if (String(editForm.password || "").trim()) {
          fd.append("password", String(editForm.password).trim());
        }

        // Common fields
        fd.append("userID", editForm.userID || "");
        fd.append("bankAccountNumber", editForm.bankAccountNumber || "");
        fd.append("ifscCode", editForm.ifscCode || "");
        fd.append("accountHolderName", editForm.accountHolderName || "");
        fd.append("panNumber", editForm.panNumber || "");
        fd.append("upiId", editForm.upiId || "");
        fd.append("status", String(editForm.status));

        // ✅ New missing fields
        fd.append("gender", editForm.gender || "");
        fd.append("dateOfBirth", editForm.dateOfBirth || "");
        fd.append("nomineeName", editForm.nomineeName || "");
        fd.append("nomineeRelation", editForm.nomineeRelation || "");
        fd.append("nomineePhone", editForm.nomineePhone || "");
        fd.append("bankName", editForm.bankName || "");
        fd.append("bankBranch", editForm.bankBranch || "");
        fd.append("bankAccountType", editForm.bankAccountType || "");
        fd.append("adharNumber", editForm.adharNumber || "");

        // Files
        if (picFile) fd.append("profilePic", picFile);
        if (bankPhotoFile) fd.append("bankPhoto", bankPhotoFile);
        if (panPhotoFile) fd.append("panPhoto", panPhotoFile);
        if (aadharPhotoFile) fd.append("aadharPhoto", aadharPhotoFile);

        await axiosInstance.put(`/api/users/${selectedUser.id}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        const payload = {
          ...editForm,
          // ensured trimmed password if typing
          password: String(editForm.password || "").trim() || undefined,
        };

        await axiosInstance.put(`/api/users/${selectedUser.id}`, payload);
      }

      closeEdit();
      fetchUsers(search);
    } catch (err) {
      console.log("PUT /api/users/:id error", err);
      alert(err?.response?.data?.msg || "Update failed");
    } finally {
      setEditSaving(false);
    }
  };

  // ---------- ADD ADMIN / STAFF ----------
  const saveAdd = async () => {
    if (!addForm.name.trim() || !addForm.email.trim() || !addForm.phone.trim() || !addForm.password.trim()) {
      alert("Please fill in all required fields (Name, Email, Phone, Password)");
      return;
    }
    try {
      setAddSaving(true);

      const hasFiles = addBankPhotoFile || addPanPhotoFile || addAadharPhotoFile || addPicFile;

      if (hasFiles) {
        const fd = new FormData();
        fd.append("name", addForm.name);
        fd.append("email", addForm.email);
        fd.append("phone", addForm.phone);
        fd.append("password", addForm.password);
        fd.append("role", addForm.role);
        if (addForm.gender) fd.append("gender", addForm.gender);
        if (addForm.dateOfBirth) fd.append("dateOfBirth", addForm.dateOfBirth);
        if (addForm.bankAccountNumber) fd.append("bankAccountNumber", addForm.bankAccountNumber);
        if (addForm.ifscCode) fd.append("ifscCode", addForm.ifscCode);
        if (addForm.accountHolderName) fd.append("accountHolderName", addForm.accountHolderName);
        if (addForm.bankName) fd.append("bankName", addForm.bankName);
        if (addForm.bankBranch) fd.append("bankBranch", addForm.bankBranch);
        if (addForm.bankAccountType) fd.append("bankAccountType", addForm.bankAccountType);
        if (addForm.panNumber) fd.append("panNumber", addForm.panNumber);
        if (addForm.adharNumber) fd.append("adharNumber", addForm.adharNumber);
        if (addForm.upiId) fd.append("upiId", addForm.upiId);
        if (addForm.nomineeName) fd.append("nomineeName", addForm.nomineeName);
        if (addForm.nomineeRelation) fd.append("nomineeRelation", addForm.nomineeRelation);
        if (addForm.nomineePhone) fd.append("nomineePhone", addForm.nomineePhone);
        if (addBankPhotoFile) fd.append("bankPhoto", addBankPhotoFile);
        if (addPanPhotoFile) fd.append("panPhoto", addPanPhotoFile);
        if (addAadharPhotoFile) fd.append("aadharPhoto", addAadharPhotoFile);
        if (addPicFile) fd.append("profilePic", addPicFile);

        await axiosInstance.post("/api/auth/register", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        // ✅ Use the existing register endpoint which supports role field
        await axiosInstance.post("/api/auth/register", {
          name: addForm.name, email: addForm.email, phone: addForm.phone,
          password: addForm.password, role: addForm.role,
          gender: addForm.gender || undefined,
          dateOfBirth: addForm.dateOfBirth || undefined,
          bankAccountNumber: addForm.bankAccountNumber || undefined,
          ifscCode: addForm.ifscCode || undefined,
          accountHolderName: addForm.accountHolderName || undefined,
          bankName: addForm.bankName || undefined,
          bankBranch: addForm.bankBranch || undefined,
          bankAccountType: addForm.bankAccountType || undefined,
          panNumber: addForm.panNumber || undefined,
          adharNumber: addForm.adharNumber || undefined,
          upiId: addForm.upiId || undefined,
          nomineeName: addForm.nomineeName || undefined,
          nomineeRelation: addForm.nomineeRelation || undefined,
          nomineePhone: addForm.nomineePhone || undefined,
        });
      }

      setAddOpen(false);
      setAddBankPhotoFile(null); setAddBankPhotoPreview("");
      setAddPanPhotoFile(null); setAddPanPhotoPreview("");
      setAddAadharPhotoFile(null); setAddAadharPhotoPreview("");
      setAddPicFile(null); setAddPicPreview("");
      fetchUsers(search);
    } catch (err) {
      console.log("POST /api/auth/register error", err);
      alert(err?.response?.data?.msg || "Failed to create user");
    } finally {
      setAddSaving(false);
    }
  };
  return (
    <div style={styles.page}>
      <div style={styles.topbar}>
        <h2 style={{ margin: 0 }}>Users</h2>
        <button onClick={() => navigate("/admin/dashboard")} style={styles.btn}>
          ← Dashboard
        </button>
      </div>

      {/* ✅ ROLE TABS (Admin Only) */}
      {userRole === "ADMIN" && (
        <div style={styles.tabContainer}>
          <button
            style={filterRole === "USER" ? styles.activeTab : styles.tab}
            onClick={() => setFilterRole("USER")}
          >
            Users
          </button>
          <button
            style={filterRole === "OTHERS" ? styles.activeTab : styles.tab}
            onClick={() => setFilterRole("OTHERS")}
          >
            Admin / Staff
          </button>

          {/* ✅ Add + button – only on OTHERS tab, admin only */}
          {filterRole === "OTHERS" && userRole === "ADMIN" && (
            <button
              style={{ marginLeft: "auto", padding: "7px 14px", background: "#1a73e8", color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 13 }}
              onClick={() => { setAddForm({ name: "", email: "", phone: "", password: "", role: "STAFF" }); setAddOpen(true); }}
            >
              + Add Admin / Staff
            </button>
          )}
        </div>
      )}

      {/* ✅ Search UI & Status Filter */}
      <div style={{ display: "flex", gap: 12, marginBottom: 12, maxWidth: 800 }}>
        <div style={{ ...styles.searchBar, marginBottom: 0, flex: 1, maxWidth: "none" }}>
          <div style={styles.searchIcon}>🔍</div>
          <input
            style={styles.searchInput}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name / email / phone / userType / userID..."
          />
          {search.trim() ? (
            <button type="button" style={styles.clearBtn} onClick={() => setSearch("")} title="Clear">
              ✕
            </button>
          ) : null}
        </div>

        <select
          style={{ padding: "0 16px", borderRadius: 16, border: "1px solid #e6e6e6", background: "#fff", outline: "none", cursor: "pointer", fontWeight: 600, boxShadow: "0 8px 22px rgba(0,0,0,0.06)", height: 48 }}
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="ALL">All Status</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
        </select>
      </div>

      <div style={styles.card}>
        {loading ? (
          <div style={styles.info}>Loading...</div>
        ) : (
          <>
            <div style={styles.meta}>
              Displaying: <b>{filterRole === "USER" ? "Regular Users" : "Admins & Staff"}</b> | Total: <b>{total}</b>
            </div>

            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {/* ✅ SAME DESIGN, just adding new columns */}
                    {[
                      "ID",
                      "Pic",
                      "Name",
                      "Email",
                      "Phone",
                      "UserID",
                      "UserType",

                      "Password",
                      "Actions",
                    ].map((h) => (
                      <th key={h} style={styles.th}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={11} style={styles.empty}>
                        No users found
                      </td>
                    </tr>
                  ) : (
                    users.map((u) => (
                      <tr key={u.id}>
                        <td style={styles.td}>{u.id}</td>

                        {/* Pic */}
                        <td style={styles.td}>
                          {u.profilePic ? (
                            <img
                              src={buildImgUrl(u.profilePic)}
                              alt="pic"
                              style={styles.avatar}
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            <div style={styles.avatarFallback}>👤</div>
                          )}
                        </td>

                        <td style={styles.td}>{safe(u.name)}</td>
                        <td style={styles.td}>{safe(u.email)}</td>
                        <td style={styles.td}>{safe(u.phone)}</td>

                        {/* ✅ UserID beside Phone */}
                        <td style={styles.td}>{safe(u.userID)}</td>

                        <td style={styles.td}>{safe(u.userType)}</td>

                        {/* ✅ Wallet (tries many shapes) 
                        <td style={styles.td}>
                          ₹{safe(u.walletBalance ?? u.wallet?.balance ?? u.balance ?? 0, "0")}
                        </td>

                        <td style={styles.td}>
                          {u.isActive === false ? (
                            <span style={{ fontWeight: 900, color: "#b91c1c" }}>Inactive</span>
                          ) : (
                            <span style={{ fontWeight: 900, color: "#166534" }}>Active</span>
                          )}
                        </td>*/}

                        {/* Password masked */}
                        <td style={styles.td}>{u.password ? "••••••" : "-"}</td>

                        <td style={styles.td}>
                          <div style={styles.actions}>
                            <button style={styles.actionBtn} onClick={() => openView(u.id)}>
                              View
                            </button>
                            {userRole === "ADMIN" && (
                              <button style={styles.actionBtn} onClick={() => openEdit(u)}>
                                Edit
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* ✅ RIGHT DRAWER (VIEW) */}
      {drawerOpen && (
        <div style={styles.drawerOverlay} onClick={closeView}>
          <div style={styles.drawer} onClick={(e) => e.stopPropagation()}>
            <div style={styles.drawerHeader}>
              <h3 style={{ margin: 0 }}>User Details</h3>
              <button style={styles.btn} onClick={closeView}>
                ✕
              </button>
            </div>

            {viewLoading ? (
              <div style={styles.info}>Loading...</div>
            ) : selectedUser ? (
              <div style={styles.drawerBody}>
                {/* profile pic */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                  {selectedUser.profilePic ? (
                    <img
                      src={buildImgUrl(selectedUser.profilePic)}
                      alt="pic"
                      style={{ ...styles.avatar, width: 58, height: 58 }}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <div style={{ ...styles.avatarFallback, width: 58, height: 58 }}>👤</div>
                  )}
                  <div>
                    <div style={{ fontWeight: 800 }}>{safe(selectedUser.name)}</div>
                    <div style={{ opacity: 0.75, fontSize: 13 }}>{safe(selectedUser.userType)}</div>
                    {/* ✅ Show userID here also */}
                    <div style={{ opacity: 0.85, fontSize: 13, marginTop: 2 }}>
                      UserID: <b>{safe(selectedUser.userID)}</b>
                    </div>
                  </div>
                </div>

                <Row label="ID" value={selectedUser.id} />
                <Row label="UserID" value={selectedUser.userID} />
                <Row label="Name" value={selectedUser.name} />
                <Row label="Email" value={selectedUser.email} />
                <Row label="Phone" value={selectedUser.phone} />
                <Row label="Role" value={selectedUser.role} />
                <Row label="UserType" value={selectedUser.userType} />
                <Row
                  label="Wallet"
                  value={`₹${safe(
                    selectedUser.walletBalance ?? selectedUser.wallet?.balance ?? selectedUser.balance ?? 0,
                    "0"
                  )}`}
                />
                <Row label="Status" value={selectedUser.status === "INACTIVE" ? "Inactive" : "Active"} />

                {/* ✅ Personal Details Section */}
                <div style={{ marginTop: 14, fontWeight: 900, color: "#111" }}>Personal Details</div>
                <div style={{ marginTop: 6 }} />
                <Row label="Gender" value={selectedUser.gender} />
                <Row label="DOB" value={selectedUser.dateOfBirth} />
                <Row label="Activation" value={selectedUser.activationDate ? new Date(selectedUser.activationDate).toLocaleDateString() : "-"} />

                {/* password show/hide */}
                <div style={styles.row}>
                  <div style={styles.rowLabel}>Password</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={styles.rowValue}>
                      {selectedUser.password
                        ? viewPwdShow
                          ? String(selectedUser.password)
                          : "••••••••"
                        : "-"}
                    </div>
                    <button
                      type="button"
                      style={styles.eyeBtn}
                      onClick={() => setViewPwdShow((s) => !s)}
                      disabled={!selectedUser.password}
                      title={viewPwdShow ? "Hide" : "Show"}
                    >
                      {viewPwdShow ? "🙈" : "👁️"}
                    </button>
                  </div>
                </div>

                {/* ✅ Bank details section (view) */}
                <div style={{ marginTop: 14, fontWeight: 900, color: "#111" }}>Bank & KYC Details</div>
                <div style={{ marginTop: 6 }} />
                <Row label="Account Holder" value={selectedUser.accountHolderName} />
                <Row label="Account Number" value={selectedUser.bankAccountNumber} />
                <Row label="Bank Name" value={selectedUser.bankName} />
                <Row label="Branch" value={selectedUser.bankBranch} />
                <Row label="Account Type" value={selectedUser.bankAccountType} />
                <Row label="IFSC" value={selectedUser.ifscCode} />
                <Row label="PAN" value={selectedUser.panNumber} />
                <Row label="Aadhaar" value={selectedUser.adharNumber} />
                <Row label="UPI" value={selectedUser.upiId} />

                {/* ✅ KYC Photos (View) */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
                  <div>
                    <div style={styles.rowLabel}>Bank Photo</div>
                    {selectedUser.bankPhoto ? (
                      <img src={buildImgUrl(selectedUser.bankPhoto)} alt="bank" style={{ width: "100%", borderRadius: 8, marginTop: 4, border: "1px solid #eee" }} />
                    ) : <div style={{ fontSize: 12, color: "#999", marginTop: 4 }}>Not uploaded</div>}
                  </div>
                  <div>
                    <div style={styles.rowLabel}>PAN Photo</div>
                    {selectedUser.panPhoto ? (
                      <img src={buildImgUrl(selectedUser.panPhoto)} alt="pan" style={{ width: "100%", borderRadius: 8, marginTop: 4, border: "1px solid #eee" }} />
                    ) : <div style={{ fontSize: 12, color: "#999", marginTop: 4 }}>Not uploaded</div>}
                  </div>
                  <div>
                    <div style={styles.rowLabel}>Aadhar Photo</div>
                    {selectedUser.aadharPhoto ? (
                      <img src={buildImgUrl(selectedUser.aadharPhoto)} alt="aadhar" style={{ width: "100%", borderRadius: 8, marginTop: 4, border: "1px solid #eee" }} />
                    ) : <div style={{ fontSize: 12, color: "#999", marginTop: 4 }}>Not uploaded</div>}
                  </div>
                </div>

                {/* ✅ Nominee Section (View) */}
                <div style={{ marginTop: 14, fontWeight: 900, color: "#111" }}>Nominee Details</div>
                <div style={{ marginTop: 6 }} />
                <Row label="Nominee Name" value={selectedUser.nomineeName} />
                <Row label="Relation" value={selectedUser.nomineeRelation} />
                <Row label="Phone" value={selectedUser.nomineePhone} />

                <div style={{ marginTop: 14 }} />
                <Row
                  label="Created"
                  value={selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleString() : "-"}
                />
                <Row
                  label="Updated"
                  value={selectedUser.updatedAt ? new Date(selectedUser.updatedAt).toLocaleString() : "-"}
                />

                <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                  <button style={styles.actionBtn} onClick={() => openEdit(selectedUser)}>
                    Edit
                  </button>
                </div>
              </div>
            ) : (
              <div style={styles.info}>No data</div>
            )}
          </div>
        </div>
      )}

      {/* ✅ EDIT MODAL */}
      {editOpen && (
        <div style={styles.modalOverlay} onClick={closeEdit}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0 }}>Edit User</h3>
              <button style={styles.btn} onClick={closeEdit}>
                ✕
              </button>
            </div>

            <div style={styles.modalBody}>
              {/* Profile Pic upload */}
              <label style={styles.label}>Profile Pic</label>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {picPreview ? (
                  <img src={picPreview} alt="preview" style={{ ...styles.avatar, width: 52, height: 52 }} />
                ) : (
                  <div style={{ ...styles.avatarFallback, width: 52, height: 52 }}>👤</div>
                )}
                <input type="file" accept="image/*" onChange={onPickPic} />
              </div>

              <label style={styles.label}>Name</label>
              <input
                style={styles.modalInput}
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />

              <label style={styles.label}>Email</label>
              <input
                style={styles.modalInput}
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />

              <label style={styles.label}>Phone</label>
              <input
                style={styles.modalInput}
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
              />

              {/* ✅ NEW: UserID */}
              <label style={styles.label}>UserID</label>
              <input
                style={styles.modalInput}
                value={editForm.userID}
                onChange={(e) => setEditForm({ ...editForm, userID: e.target.value })}
                placeholder="SUN146762"
              />

              {/* UserType */}
              <label style={styles.label}>User Type</label>
              <select
                style={styles.modalInput}
                value={editForm.userType}
                onChange={(e) => setEditForm({ ...editForm, userType: e.target.value })}
              >
                {USER_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>

              {/* ✅ NEW: Status */}
              <label style={styles.label}>Status</label>
              <select
                style={styles.modalInput}
                value={editForm.status}
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
              >
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>

              {/* ✅ NEW: Personal Details in edit */}
              <div style={{ marginTop: 16, fontWeight: 900, color: "#111" }}>Personal Details</div>

              <label style={styles.label}>Gender</label>
              <select
                style={styles.modalInput}
                value={editForm.gender}
                onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })}
              >
                <option value="">Select Gender</option>
                <option value="MALE">Male</option>
                <option value="FEMALE">Female</option>
                <option value="OTHER">Other</option>
              </select>

              <label style={styles.label}>Date of Birth</label>
              <input
                type="date"
                style={styles.modalInput}
                value={editForm.dateOfBirth}
                onChange={(e) => setEditForm({ ...editForm, dateOfBirth: e.target.value })}
              />

              {/* Password (show/hide) */}
              <label style={styles.label}>Password</label>
              <div style={styles.pwdWrap}>
                <input
                  style={styles.pwdInput}
                  type={editPwdShow ? "text" : "password"}
                  value={editForm.password}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                />
                <button
                  type="button"
                  style={styles.eyeBtn}
                  onClick={() => setEditPwdShow((s) => !s)}
                  title={editPwdShow ? "Hide" : "Show"}
                >
                  {editPwdShow ? "🙈" : "👁️"}
                </button>
              </div>

              {/* ✅ NEW: Bank details in edit */}
              <div style={{ marginTop: 12, fontWeight: 900, color: "#111" }}>Bank Details</div>

              <label style={styles.label}>Account Holder Name</label>
              <input
                style={styles.modalInput}
                value={editForm.accountHolderName}
                onChange={(e) => setEditForm({ ...editForm, accountHolderName: e.target.value })}
                placeholder="Account holder"
              />

              <label style={styles.label}>Bank Account Number</label>
              <input
                style={styles.modalInput}
                value={editForm.bankAccountNumber}
                onChange={(e) => setEditForm({ ...editForm, bankAccountNumber: e.target.value })}
                placeholder="1234567890"
              />

              <label style={styles.label}>IFSC Code</label>
              <input
                style={styles.modalInput}
                value={editForm.ifscCode}
                onChange={(e) => setEditForm({ ...editForm, ifscCode: e.target.value })}
                placeholder="SBIN0000000"
              />

              <label style={styles.label}>PAN Number</label>
              <input
                style={styles.modalInput}
                value={editForm.panNumber}
                onChange={(e) => setEditForm({ ...editForm, panNumber: e.target.value })}
                placeholder="ABCDE1234F"
              />

              <label style={styles.label}>UPI ID</label>
              <input
                style={styles.modalInput}
                value={editForm.upiId}
                onChange={(e) => setEditForm({ ...editForm, upiId: e.target.value })}
                placeholder="user@upi"
              />

              <label style={styles.label}>Bank Name</label>
              <input
                style={styles.modalInput}
                value={editForm.bankName}
                onChange={(e) => setEditForm({ ...editForm, bankName: e.target.value })}
                placeholder="HDFC Bank"
              />

              <label style={styles.label}>Bank Branch</label>
              <input
                style={styles.modalInput}
                value={editForm.bankBranch}
                onChange={(e) => setEditForm({ ...editForm, bankBranch: e.target.value })}
                placeholder="Ameerpet Branch"
              />

              <label style={styles.label}>Account Type</label>
              <input
                style={styles.modalInput}
                value={editForm.bankAccountType}
                onChange={(e) => setEditForm({ ...editForm, bankAccountType: e.target.value })}
                placeholder="Savings / Current"
              />

              <label style={styles.label}>Aadhaar Number</label>
              <input
                style={styles.modalInput}
                value={editForm.adharNumber}
                onChange={(e) => setEditForm({ ...editForm, adharNumber: e.target.value })}
                placeholder="1234 5678 9012"
              />

              {/* ✅ Document Photo Uploads */}
              <div style={{ marginTop: 12, fontWeight: 900, color: "#111" }}>KYC Documents (Photos)</div>

              <label style={styles.label}>Bank Passbook / Cheque</label>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {bankPhotoPreview ? (
                  <img src={bankPhotoPreview} alt="bank_preview" style={{ width: 60, borderRadius: 4 }} />
                ) : <div style={{ fontSize: 11, color: "#999" }}>No image</div>}
                <input type="file" accept="image/*" onChange={onPickBankPhoto} />
              </div>

              <label style={styles.label}>PAN Card Photo</label>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {panPhotoPreview ? (
                  <img src={panPhotoPreview} alt="pan_preview" style={{ width: 60, borderRadius: 4 }} />
                ) : <div style={{ fontSize: 11, color: "#999" }}>No image</div>}
                <input type="file" accept="image/*" onChange={onPickPanPhoto} />
              </div>

              <label style={styles.label}>Aadhar Card Photo</label>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {aadharPhotoPreview ? (
                  <img src={aadharPhotoPreview} alt="aadhar_preview" style={{ width: 60, borderRadius: 4 }} />
                ) : <div style={{ fontSize: 11, color: "#999" }}>No image</div>}
                <input type="file" accept="image/*" onChange={onPickAadharPhoto} />
              </div>

              {/* ✅ Nominee Details in edit */}
              <div style={{ marginTop: 16, fontWeight: 900, color: "#111" }}>Nominee Details</div>

              <label style={styles.label}>Nominee Name</label>
              <input
                style={styles.modalInput}
                value={editForm.nomineeName}
                onChange={(e) => setEditForm({ ...editForm, nomineeName: e.target.value })}
              />

              <label style={styles.label}>Nominee Relation</label>
              <input
                style={styles.modalInput}
                value={editForm.nomineeRelation}
                onChange={(e) => setEditForm({ ...editForm, nomineeRelation: e.target.value })}
              />

              <label style={styles.label}>Nominee Phone</label>
              <input
                style={styles.modalInput}
                value={editForm.nomineePhone}
                onChange={(e) => setEditForm({ ...editForm, nomineePhone: e.target.value })}
              />

              {/* Wallet (read-only display) */}
              <div style={{ marginTop: 10, fontSize: 13, color: "#444" }}>
                Wallet: <b>₹{safe(editForm.walletBalance ?? 0, "0")}</b> (read-only)
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                <button style={styles.btn} onClick={closeEdit} disabled={editSaving}>
                  Cancel
                </button>
                <button style={styles.primaryBtn} onClick={saveEdit} disabled={editSaving}>
                  {editSaving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ✅ ADD ADMIN / STAFF MODAL */}
      {addOpen && (
        <div style={styles.modalOverlay} onClick={() => setAddOpen(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0 }}>Add Admin / Staff</h3>
              <button style={styles.btn} onClick={() => setAddOpen(false)}>✕</button>
            </div>
            <div style={styles.modalBody}>

              {/* Profile Pic */}
              <label style={styles.label}>Profile Pic</label>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 8 }}>
                {addPicPreview ? (
                  <img src={addPicPreview} alt="preview" style={{ width: 56, height: 56, borderRadius: 10, objectFit: "cover", border: "1px solid #eee" }} />
                ) : (
                  <div style={{ width: 56, height: 56, borderRadius: 10, background: "#f4f4f4", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, border: "1px solid #eee" }}>👤</div>
                )}
                <input type="file" accept="image/*" onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setAddPicFile(f);
                  setAddPicPreview(f ? URL.createObjectURL(f) : "");
                }} />
              </div>

              <input
                style={styles.modalInput}
                placeholder="Enter full name"
                value={addForm.name}
                onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
              />

              <label style={styles.label}>Email *</label>
              <input
                style={styles.modalInput}
                type="email"
                placeholder="Enter email"
                value={addForm.email}
                onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
              />

              <label style={styles.label}>Phone *</label>
              <input
                style={styles.modalInput}
                placeholder="Enter phone number"
                value={addForm.phone}
                onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })}
              />

              <label style={styles.label}>Password *</label>
              <div style={styles.pwdWrap}>
                <input
                  style={styles.pwdInput}
                  type={addPwdShow ? "text" : "password"}
                  placeholder="Enter password"
                  value={addForm.password}
                  onChange={(e) => setAddForm({ ...addForm, password: e.target.value })}
                />
                <button
                  type="button"
                  style={styles.eyeBtn}
                  onClick={() => setAddPwdShow((s) => !s)}
                  title={addPwdShow ? "Hide" : "Show"}
                >
                  {addPwdShow ? "🙈" : "👁️"}
                </button>
              </div>

              <label style={styles.label}>Role *</label>
              <select
                style={styles.modalInput}
                value={addForm.role}
                onChange={(e) => setAddForm({ ...addForm, role: e.target.value })}
              >
                <option value="STAFF">Staff</option>
                <option value="ADMIN">Admin</option>
              </select>

              {/* ✅ Personal Details */}
              <div style={{ marginTop: 16, fontWeight: 900, color: "#111" }}>Personal Details</div>

              <label style={styles.label}>Gender</label>
              <select
                style={styles.modalInput}
                value={addForm.gender}
                onChange={(e) => setAddForm({ ...addForm, gender: e.target.value })}
              >
                <option value="">Select Gender</option>
                <option value="MALE">Male</option>
                <option value="FEMALE">Female</option>
                <option value="OTHER">Other</option>
              </select>

              <label style={styles.label}>Date of Birth</label>
              <input
                type="date"
                style={styles.modalInput}
                value={addForm.dateOfBirth}
                onChange={(e) => setAddForm({ ...addForm, dateOfBirth: e.target.value })}
              />

              {/* ✅ Bank & KYC Details */}
              <div style={{ marginTop: 16, fontWeight: 900, color: "#111" }}>Bank & KYC Details</div>

              <label style={styles.label}>Account Holder Name</label>
              <input style={styles.modalInput} placeholder="Account holder" value={addForm.accountHolderName}
                onChange={(e) => setAddForm({ ...addForm, accountHolderName: e.target.value })} />

              <label style={styles.label}>Bank Account Number</label>
              <input style={styles.modalInput} placeholder="1234567890" value={addForm.bankAccountNumber}
                onChange={(e) => setAddForm({ ...addForm, bankAccountNumber: e.target.value })} />

              <label style={styles.label}>Bank Name</label>
              <input style={styles.modalInput} placeholder="HDFC Bank" value={addForm.bankName}
                onChange={(e) => setAddForm({ ...addForm, bankName: e.target.value })} />

              <label style={styles.label}>Bank Branch</label>
              <input style={styles.modalInput} placeholder="Ameerpet Branch" value={addForm.bankBranch}
                onChange={(e) => setAddForm({ ...addForm, bankBranch: e.target.value })} />

              <label style={styles.label}>Account Type</label>
              <input style={styles.modalInput} placeholder="Savings / Current" value={addForm.bankAccountType}
                onChange={(e) => setAddForm({ ...addForm, bankAccountType: e.target.value })} />

              <label style={styles.label}>IFSC Code</label>
              <input style={styles.modalInput} placeholder="SBIN0000000" value={addForm.ifscCode}
                onChange={(e) => setAddForm({ ...addForm, ifscCode: e.target.value })} />

              <label style={styles.label}>PAN Number</label>
              <input style={styles.modalInput} placeholder="ABCDE1234F" value={addForm.panNumber}
                onChange={(e) => setAddForm({ ...addForm, panNumber: e.target.value })} />

              <label style={styles.label}>Aadhaar Number</label>
              <input style={styles.modalInput} placeholder="1234 5678 9012" value={addForm.adharNumber}
                onChange={(e) => setAddForm({ ...addForm, adharNumber: e.target.value })} />

              <label style={styles.label}>UPI ID</label>
              <input style={styles.modalInput} placeholder="user@upi" value={addForm.upiId}
                onChange={(e) => setAddForm({ ...addForm, upiId: e.target.value })} />

              {/* ✅ KYC Document Photos */}
              <div style={{ marginTop: 16, fontWeight: 900, color: "#111" }}>KYC Documents</div>

              <label style={styles.label}>Bank Passbook / Cheque</label>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {addBankPhotoPreview ? (
                  <img src={addBankPhotoPreview} alt="bank" style={{ width: 60, borderRadius: 4 }} />
                ) : <div style={{ fontSize: 11, color: "#999" }}>No image</div>}
                <input type="file" accept="image/*" onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setAddBankPhotoFile(f);
                  setAddBankPhotoPreview(f ? URL.createObjectURL(f) : "");
                }} />
              </div>

              <label style={styles.label}>PAN Card Photo</label>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {addPanPhotoPreview ? (
                  <img src={addPanPhotoPreview} alt="pan" style={{ width: 60, borderRadius: 4 }} />
                ) : <div style={{ fontSize: 11, color: "#999" }}>No image</div>}
                <input type="file" accept="image/*" onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setAddPanPhotoFile(f);
                  setAddPanPhotoPreview(f ? URL.createObjectURL(f) : "");
                }} />
              </div>

              <label style={styles.label}>Aadhar Card Photo</label>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {addAadharPhotoPreview ? (
                  <img src={addAadharPhotoPreview} alt="aadhar" style={{ width: 60, borderRadius: 4 }} />
                ) : <div style={{ fontSize: 11, color: "#999" }}>No image</div>}
                <input type="file" accept="image/*" onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setAddAadharPhotoFile(f);
                  setAddAadharPhotoPreview(f ? URL.createObjectURL(f) : "");
                }} />
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                <button style={styles.btn} onClick={() => setAddOpen(false)} disabled={addSaving}>
                  Cancel
                </button>
                <button style={styles.primaryBtn} onClick={saveAdd} disabled={addSaving}>
                  {addSaving ? "Creating..." : "Create"}
                </button>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* helpers */
function getEmptyEditForm() {
  return {
    name: "",
    email: "",
    phone: "",
    userType: "TRAINEE_ENTREPRENEUR",
    password: "",

    // ✅ NEW
    userID: "",
    bankAccountNumber: "",
    ifscCode: "",
    accountHolderName: "",
    panNumber: "",
    upiId: "",
    status: "ACTIVE",
    walletBalance: "",

    // ✅ New Fields
    gender: "",
    dateOfBirth: "",
    nomineeName: "",
    nomineeRelation: "",
    nomineePhone: "",
    bankName: "",
    bankBranch: "",
    bankAccountType: "",
    adharNumber: "",
  };
}

/* small row component */
function Row({ label, value }) {
  return (
    <div style={styles.row}>
      <div style={styles.rowLabel}>{label}</div>
      <div style={styles.rowValue}>{safe(value)}</div>
    </div>
  );
}

/* ✅ SAME STYLE you gave. Only added table minWidth for new columns */
const styles = {
  page: { padding: 20, background: "#f4f6f8", minHeight: "100vh" },
  topbar: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 },
  btn: { padding: "10px 12px", cursor: "pointer", borderRadius: 10, border: "1px solid #cfcfcf", background: "#f3f3f3" },

  // Tabs
  tabContainer: { display: "flex", gap: 10, marginBottom: 16 },
  tab: {
    padding: "8px 16px",
    cursor: "pointer",
    borderRadius: 8,
    border: "1px solid #ddd",
    background: "#fff",
    fontSize: 14,
    fontWeight: 600,
    transition: "0.2s",
  },
  activeTab: {
    padding: "8px 16px",
    cursor: "pointer",
    borderRadius: 8,
    border: "1px solid #111",
    background: "#111",
    color: "#fff",
    fontSize: 14,
    fontWeight: 600,
    transition: "0.2s",
  },

  // Search
  searchBar: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: "#fff",
    border: "1px solid #e6e6e6",
    borderRadius: 16,
    padding: "12px 14px",
    boxShadow: "0 8px 22px rgba(0,0,0,0.06)",
    marginBottom: 12,
    maxWidth: 620,
  },
  searchIcon: { fontSize: 16, opacity: 0.75 },
  searchInput: { border: "none", outline: "none", fontSize: 15, flex: 1, background: "transparent" },
  clearBtn: { border: "none", background: "#f2f2f2", borderRadius: 10, cursor: "pointer", padding: "8px 10px", fontSize: 14 },

  card: { background: "#fff", borderRadius: 12, padding: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.08)" },
  info: { padding: 10 },
  meta: { marginBottom: 10, color: "#444" },

  table: { width: "100%", borderCollapse: "collapse", minWidth: 1180 },
  th: { textAlign: "left", padding: "10px 8px", borderBottom: "1px solid #eee", fontSize: 13, color: "#444", whiteSpace: "nowrap" },
  td: { padding: "10px 8px", borderBottom: "1px solid #f2f2f2", verticalAlign: "top", whiteSpace: "nowrap" },
  empty: { padding: 16, textAlign: "center", color: "#666" },

  actions: { display: "flex", gap: 8, flexWrap: "wrap" },
  actionBtn: { padding: "8px 10px", cursor: "pointer", borderRadius: 8, border: "1px solid #ddd", background: "#fff" },
  dangerBtn: { padding: "8px 10px", cursor: "pointer", borderRadius: 8, border: "1px solid #ffcccc", background: "#ffecec" },

  // drawer
  drawerOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)", display: "flex", justifyContent: "flex-end", zIndex: 50 },
  drawer: { width: 380, maxWidth: "90vw", height: "100%", background: "#fff", padding: 14, boxShadow: "-6px 0 18px rgba(0,0,0,0.12)", overflowY: "auto" },
  drawerHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 10, borderBottom: "1px solid #eee" },
  drawerBody: { paddingTop: 12 },

  row: { display: "grid", gridTemplateColumns: "120px 1fr", gap: 10, padding: "8px 0", borderBottom: "1px dashed #f0f0f0" },
  rowLabel: { color: "#666", fontSize: 13 },
  rowValue: { color: "#111", fontWeight: 600, wordBreak: "break-word" },

  // modal
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 60, padding: "24px 16px", overflowY: "auto" },
  modal: { width: 520, maxWidth: "95vw", background: "#fff", borderRadius: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.18)", maxHeight: "90vh", display: "flex", flexDirection: "column", flexShrink: 0 },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: 14, borderBottom: "1px solid #eee", flexShrink: 0 },
  modalBody: { padding: 14, overflowY: "auto", flex: 1 },
  label: { display: "block", fontSize: 13, color: "#666", marginTop: 10, marginBottom: 6 },
  modalInput: { width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #ddd", outline: "none" },

  // confirm
  confirm: { width: 420, maxWidth: "95vw", background: "#fff", borderRadius: 12, padding: 16, boxShadow: "0 8px 24px rgba(0,0,0,0.18)" },

  avatar: { width: 34, height: 34, borderRadius: 10, objectFit: "cover", border: "1px solid #eee" },
  avatarFallback: {
    width: 34,
    height: 34,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid #eee",
    background: "#fafafa",
  },

  pwdWrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    border: "1px solid #ddd",
    borderRadius: 8,
    padding: "6px 8px",
    background: "#fff",
  },
  pwdInput: { flex: 1, border: "none", outline: "none", fontSize: 14, padding: "8px 6px", background: "transparent" },
  eyeBtn: { padding: "8px 10px", cursor: "pointer", borderRadius: 8, border: "1px solid #ddd", background: "#fff", lineHeight: 1 },

  primaryBtn: {
    padding: "10px 12px",
    cursor: "pointer",
    borderRadius: 10,
    border: "1px solid #ddd",
    background: "#f7f7f7",
    fontWeight: 800,
  },
};